import React, { useEffect, useState, useMemo } from 'react';
import api, { BASE as API_BASE } from '../services/api';
import { toast } from '../utils/toastBus';

export default function AdminStreamsModeration() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actioning, setActioning] = useState({});

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const data = await api.listStreams(); // admin receives all
      const list = Array.isArray(data?.items) ? data.items : [];
      setItems(list);
    } catch (e) {
      setError('Failed to load streams');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  const pending = useMemo(() => items.filter(i => i.moderationStatus === 'pending'), [items]);
  const rejected = useMemo(() => items.filter(i => i.moderationStatus === 'rejected'), [items]);
  const approved = useMemo(() => items.filter(i => i.moderationStatus === 'approved'), [items]);

  async function moderate(id, status) {
    try {
      setActioning(prev => ({ ...prev, [id]: true }));
      const reason = status === 'rejected' ? (prompt('Optional rejection reason?') || null) : null;
      const res = await api.moderateStream(id, { status, reason });
      if (res?.item) {
        setItems(prev => prev.map(it => it._id === id ? { ...it, moderationStatus: status, rejectionReason: reason } : it));
        toast({ type: 'success', message: `Stream ${status}` });
      } else {
        toast({ type: 'error', message: 'Moderation failed' });
      }
    } catch (e) {
      toast({ type: 'error', message: 'Moderation failed' });
    } finally {
      setActioning(prev => ({ ...prev, [id]: false }));
    }
  }

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ color: '#ff7a45', marginBottom: 8 }}>Streams Moderation</h2>
      <p style={{ color: '#9ca3af', marginBottom: 16 }}>Review and approve or reject user-generated streams.</p>

      {loading ? (
        <p style={{ color: '#9ca3af' }}>Loading...</p>
      ) : error ? (
        <p style={{ color: '#fca5a5' }}>{error}</p>
      ) : (
        <div style={{ display: 'grid', gap: 16 }}>
          <section style={{ background: '#111827', border: '1px solid #374151', borderRadius: 12, padding: 16 }}>
            <h3 style={{ margin: 0, color: '#e5e7eb', marginBottom: 8 }}>Pending ({pending.length})</h3>
            {pending.length === 0 ? (
              <p style={{ color: '#9ca3af' }}>No pending streams.</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #374151' }}>
                    <th style={{ textAlign: 'left', padding: '8px 0', color: '#9ca3af', fontWeight: 500 }}>Title</th>
                    <th style={{ textAlign: 'left', padding: '8px 0', color: '#9ca3af', fontWeight: 500 }}>Visibility</th>
                    <th style={{ textAlign: 'left', padding: '8px 0', color: '#9ca3af', fontWeight: 500 }}>Created</th>
                    <th style={{ textAlign: 'left', padding: '8px 0', color: '#9ca3af', fontWeight: 500 }}>Preview</th>
                    <th style={{ textAlign: 'left', padding: '8px 0', color: '#9ca3af', fontWeight: 500 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pending.map(it => (
                    <tr key={it._id} style={{ borderBottom: '1px solid #1f2937' }}>
                      <td style={{ padding: '10px 0', color: '#e5e7eb' }}>{it.title}</td>
                      <td style={{ padding: '10px 0', color: '#9ca3af' }}>{it.visibility || 'public'}</td>
                      <td style={{ padding: '10px 0', color: '#9ca3af' }}>{new Date(it.createdAt).toLocaleString()}</td>
                      <td style={{ padding: '10px 0' }}>
                        {it.audioPath ? (
                          <audio
                            controls
                            src={String(it.audioPath).startsWith('/uploads/') ? `${API_BASE}${it.audioPath}` : it.audioPath}
                            style={{ height: 28, maxWidth: 260 }}
                          />
                        ) : (
                          <span style={{ color: '#6b7280' }}>No audio</span>
                        )}
                      </td>
                      <td style={{ padding: '10px 0' }}>
                        <button disabled={!!actioning[it._id]} onClick={() => moderate(it._id, 'approved')} style={{ background: '#10b981', color: '#0b0b0e', padding: '6px 10px', border: 0, borderRadius: 6, marginRight: 8, cursor: 'pointer' }}>Approve</button>
                        <button disabled={!!actioning[it._id]} onClick={() => moderate(it._id, 'rejected')} style={{ background: '#ef4444', color: '#0b0b0e', padding: '6px 10px', border: 0, borderRadius: 6, cursor: 'pointer' }}>Reject</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section style={{ background: '#111827', border: '1px solid #374151', borderRadius: 12, padding: 16 }}>
            <h3 style={{ margin: 0, color: '#e5e7eb', marginBottom: 8 }}>Rejected ({rejected.length})</h3>
            {rejected.length === 0 ? (
              <p style={{ color: '#9ca3af' }}>No rejected streams.</p>
            ) : (
              <ul style={{ margin: 0, paddingLeft: 16, color: '#9ca3af' }}>
                {rejected.map(it => (
                  <li key={it._id} style={{ marginBottom: 6 }}>
                    {it.title} — reason: <span style={{ color: '#fca5a5' }}>{it.rejectionReason || 'n/a'}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section style={{ background: '#111827', border: '1px solid #374151', borderRadius: 12, padding: 16 }}>
            <h3 style={{ margin: 0, color: '#e5e7eb', marginBottom: 8 }}>Approved ({approved.length})</h3>
            {approved.length === 0 ? (
              <p style={{ color: '#9ca3af' }}>No approved streams.</p>
            ) : (
              <ul style={{ margin: 0, paddingLeft: 16, color: '#9ca3af', listStyle: 'none' }}>
                {approved.map(it => {
                  const playUrl = it.audioPath ? (String(it.audioPath).startsWith('/uploads/') ? `${API_BASE}${it.audioPath}` : it.audioPath) : null;
                  return (
                    <li key={it._id} style={{ marginBottom: 10, padding: '10px 0', borderBottom: '1px solid #1f2937' }}>
                      <div style={{ color: '#e5e7eb', fontWeight: 500 }}>{it.title || 'Untitled'}</div>
                      <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 6 }}>
                        {new Date(it.createdAt).toLocaleString()} • {it.visibility || 'public'}
                      </div>
                      {playUrl ? (
                        <audio controls src={playUrl} style={{ height: 28, maxWidth: 360 }} />
                      ) : (
                        <span style={{ color: '#6b7280' }}>No audio</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
